import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

const FlightComplete = ({ onReplay, onExit }) => {
  // Memoized so the star field is generated once, not on every re-render.
  // Previously these were plain Math.random() calls in the render body,
  // so if this component ever re-rendered while mounted, all 24 stars
  // would instantly jump to new random positions, same issue as the
  // Loader's particles had.
  const stars = useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => ({
        id: i,
        x: Math.random() * 100,
        y: Math.random() * 100,
        size: Math.random() * 6 + 2,
        delay: Math.random() * 1.5,
        repeatDelay: Math.random() * 3,
      })),
    []
  );

  return (
    <motion.div
      className="fc-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      role="dialog"
      aria-modal="true"
      aria-label="Flight complete"
    >
      {/* Celebration particles */}
      {stars.map(s => (
        <motion.div
          key={s.id}
          className="fc-star"
          style={{ left: `${s.x}%`, top: `${s.y}%`, width: s.size, height: s.size }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: [0, 1, 0], scale: [0, 1.5, 0], y: [-20, -80] }}
          transition={{ delay: s.delay, duration: 1.8, repeat: Infinity, repeatDelay: s.repeatDelay }}
        />
      ))}

      <motion.div
        className="fc-panel"
        initial={{ y: 80, opacity: 0, scale: 0.9 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, type: 'spring', stiffness: 260, damping: 26 }}
      >
        <div className="fc-glow" />

        <motion.div
          className="fc-icon"
          animate={{ rotate: [0, 10, -10, 5, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
        >
          ✈
        </motion.div>

        <div className="fc-eyebrow">FLIGHT PLAN COMPLETE</div>
        <h2 className="fc-title">You've explored the full portfolio!</h2>
        <p className="fc-body">
          Every checkpoint has been discovered. You've seen the intro, the journey, the technical arsenal,
          the projects, and even requested landing clearance. Well flown.
        </p>

        <div className="fc-badge-row">
          {['Introduction', 'About', 'Skills', 'Projects', 'Contact'].map((b, i) => (
            <motion.div
              key={i}
              className="fc-badge"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 + i * 0.1 }}
            >
              {b}
            </motion.div>
          ))}
        </div>

        <div className="fc-actions">
          <motion.button
            className="fc-btn fc-btn--exit"
            onClick={onExit}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            View Portfolio
          </motion.button>
          <motion.button
            className="fc-btn fc-btn--replay"
            onClick={onReplay}
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.97 }}
          >
            ↺ Replay Flight
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default FlightComplete;